import { ComboboxItem } from '@mantine/core';
import { ReactNode, useMemo } from 'react';
import { FieldValues, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormControlInput } from '@/core/components';
import { CustomSelect } from '@/core/components/Select';

interface NamedOption {
  id: number;
  name: string;
}

export interface AsyncSelectControlProps<T extends FieldValues> extends FormControlInput<T> {
  withAsterisk?: boolean;

  label: string;
  description?: string;
  placeholder?: string;

  isDisabled?: boolean;

  options: NamedOption[];

  isLoading: boolean;
  isError: boolean;

  nothingFoundMessage?: ReactNode;
  limit?: number;
  clearable?: boolean;
}

function normalize(op: NamedOption): ComboboxItem {
  return {
    value: String(op.id),
    label: op.name,
  };
}

function reverse(it: ComboboxItem | null): NamedOption | null {
  if (it == null) return null;

  return {
    id: Number(it.value),
    name: it.label,
  };
}

export function AsyncSelectControl<T extends FieldValues>(props: AsyncSelectControlProps<T>) {
  const {
    withAsterisk,
    label,
    placeholder,
    description,
    name,
    isSubmitting,
    isDisabled,
    options,
    isError,
    isLoading,
    nothingFoundMessage,
    limit,
    clearable,
    control,
  } = props;
  const {
    field,
    fieldState: { error },
  } = useController({ name, control });
  const { t } = useTranslation();

  const normalizedOptions = useMemo(() => (options ?? []).map(normalize), [options]);

  const normalizedOnChange = (_: string | null, option: ComboboxItem | null) => {
    field.onChange(reverse(option));
  };

  return (
    <CustomSelect
      data={normalizedOptions}
      ref={field.ref}
      name={field.name}
      value={field.value ? String(field.value.id) : null}
      onChange={normalizedOnChange}
      onDropdownClose={field.onBlur}
      label={label}
      description={description}
      placeholder={placeholder ?? label}
      withAsterisk={withAsterisk}
      error={error?.message ?? (isError ? t('label.serverError') : null)}
      disabled={isSubmitting || isDisabled || isLoading}
      flex={1}
      searchable
      nothingFoundMessage={nothingFoundMessage}
      limit={limit}
      clearable={clearable}
      clearButtonProps={{ 'aria-label': t('button.clear'), 'aria-hidden': false }}
    />
  );
}
